import type { DepthComponents } from './depthMath';
import { mirrorFpOffset } from './tileMath';
import { MAP_WIDTH_TILES } from './grid';

// ── Placement tolerance ("люфт") ──
// Furniture moves freely, so lining an item up tile-perfectly against a neighbour,
// a wall or a table edge would be fiddly. So a placement may be a little off:
//   * the CORE of the footprint (tiles whose 4 neighbours are all in it) must be
//     fully fine — free / on the surface / inside the room;
//   * of the outer RING, at most RING_TOLERANCE of the item's tiles may be off.
// The budget is a share of the WHOLE footprint, so a small item gets a small
// tolerance: a 3×4 skeleton may overlap 1 tile, not slip into a 1-tile gap; a
// 12×8 table may overlap a row's worth along one edge. A footprint too thin to
// have a core keeps every tile as core — a vase cannot stand inside a cupboard.

/** Share of a footprint's tiles allowed to be off, taken from its edge ring only. */
export const RING_TOLERANCE = 0.15;

/** A footprint tile in absolute tile coords, flagged core vs edge ring. */
export type PlacedTile = { x: number; y: number; core: boolean };

/** Footprint offsets → absolute tiles around the centre, with core/ring flags. */
export function placedFootprintTiles(
  footprintTiles: Array<Array<number>>,
  centerTileX: number,
  centerTileY: number,
  isMirrored: boolean,
): PlacedTile[] {
  const set = new Set(footprintTiles.map(([x, y]) => `${x},${y}`));
  const isCore = (x: number, y: number) =>
    set.has(`${x + 1},${y}`) && set.has(`${x - 1},${y}`) && set.has(`${x},${y + 1}`) && set.has(`${x},${y - 1}`);
  const anyCore = footprintTiles.some(([x, y]) => isCore(x, y));
  return footprintTiles.map(([dx, dy]) => {
    const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
    return { x: centerTileX + tdx, y: centerTileY + tdy, core: !anyCore || isCore(dx, dy) };
  });
}

/** Same for a footprint-less item's base rectangle. */
export function placedRectTiles(baseTileX: number, baseTileY: number, width: number, height: number): PlacedTile[] {
  const tiles: PlacedTile[] = [];
  const hasCore = width > 2 && height > 2;
  for (let tx = 0; tx < width; tx++) {
    for (let ty = 0; ty < height; ty++) {
      const edge = tx === 0 || ty === 0 || tx === width - 1 || ty === height - 1;
      tiles.push({ x: baseTileX + tx, y: baseTileY + ty, core: !hasCore || !edge });
    }
  }
  return tiles;
}

/** True when `isBad` hits a core tile, or more ring tiles than the tolerance allows. */
export function exceedsTolerance(tiles: PlacedTile[], isBad: (x: number, y: number) => boolean): boolean {
  const budget = Math.floor(tiles.length * RING_TOLERANCE);
  let ringBad = 0;
  for (const t of tiles) {
    if (!isBad(t.x, t.y)) continue;
    if (t.core) return true;
    ringBad += 1;
    if (ringBad > budget) return true;
  }
  return false;
}

/**
 * Is the item (partly) outside the room — in a wall or past the floor edge? Free
 * movement never blocks the pointer; a placement like this just shows red. Same
 * tolerance as conflicts, so an item may touch the wall with part of its back row.
 * Items standing on a surface are judged by the surface instead (a table pushed
 * against a wall may carry things past the floor).
 */
export function footprintOutOfRoom(
  footprintTiles: Array<Array<number>> | null | undefined,
  depth: DepthComponents,
  isMirrored: boolean,
): boolean {
  const outside = (x: number, y: number) => x < 0 || y < 0 || x >= MAP_WIDTH_TILES || y >= MAP_WIDTH_TILES;
  const frontX = Math.round(depth.tileX + 1e-6);
  const frontY = Math.round(depth.tileY + 1e-6);
  if (footprintTiles && footprintTiles.length > 0) {
    return exceedsTolerance(placedFootprintTiles(footprintTiles, frontX, frontY, isMirrored), outside);
  }
  // The UNCLAMPED base rectangle (getOccupiedBaseTile's rawBaseTile — the clamped
  // one would always look "inside"). Mirroring swaps the iso axes.
  const w = isMirrored ? depth.effectiveHeightTiles : depth.effectiveWidthTiles;
  const h = isMirrored ? depth.effectiveWidthTiles : depth.effectiveHeightTiles;
  return exceedsTolerance(placedRectTiles(frontX - w, frontY - h, w, h), outside);
}
